import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { unsubscribeUser } from "@/lib/unsubscribe";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/_home/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      userId: (search.userId as string) || "",
    };
  },
});

function UnsubscribePage() {
  const { userId } = Route.useSearch();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const router = useRouter();

  useEffect(() => {
    if (!userId) {
      setStatus("error");
      setMessage("Invalid unsubscribe link. Missing userId.");
      return;
    }

    // Process the unsubscribe request
    unsubscribeUser({ data: { userId } })
      .then((result) => {
        if (result.success) {
          setStatus("success");
          setMessage(result.message || "Successfully unsubscribed");
        } else {
          setStatus("error");
          setMessage(result.error || "An error occurred");
        }
      })
      .catch((error) => {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        setMessage("An unexpected error occurred. Please try again later.");
      });
  }, [userId]);

  const handleGoHome = () => {
    router.navigate({ to: "/" });
  };

  return (
    <div className="col-span-12 mt-4 md:mt-10 lg:mt-20 bg-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4" />
            <Heading level="h1" size="h3" className="mb-2">
              Processing Unsubscribe
            </Heading>
            <p className="text-gray-600">Please wait while we update your preferences...</p>
          </div>
        )}

        {status === "success" && (
          <div>
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <Heading level="h1" size="h3" className="mb-2">
              Successfully Unsubscribed
            </Heading>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-gray-600 mb-6">
              You will no longer receive promotional content from ProgressMade.ai. If you change
              your mind, you can always resubscribe by signing up again on our website.
            </p>
            <button
              type="button"
              onClick={handleGoHome}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition-colors"
            >
              Return to Homepage
            </button>
          </div>
        )}

        {status === "error" && (
          <div>
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <Heading level="h1" size="h3" className="mb-2">
              Error
            </Heading>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-gray-600 mb-6">
              Please try again later or contact support if the problem persists.
            </p>
            <button
              type="button"
              onClick={handleGoHome}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition-colors"
            >
              Return to Homepage
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
